// angular
import {Component, OnInit, effect, inject} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import {NgxSkeletonLoaderModule} from "ngx-skeleton-loader";
import {TranslateModule} from "@ngx-translate/core";
// components
import {MatButtonModule} from "@angular/material/button";
import {SelectComponent} from "@app/core/components/select/select.component";
import {SalesStatComponent} from "@app/+stats/components/sales-stat/sales-stat.component";
import {DatePickerComponent} from "@app/core/components/date-picker/date-picker.component";
import {DashboardSkeletonComponent} from "./dashboard-skeleton/dashboard-skeleton.component";
// services
import {UserService} from "../shared/services/user.service";
import {LayoutService} from "../layout/shared/services/layout.service";
import {GeneralStatsService} from "@app/+stats/shared/services/general-stats.service";
import {BranchService} from "@app/shared/services/branch.service";
// types
import {SelectItemS} from "../core/components/select/Item.type";
// utils
import convertDateToISO from "@app/shared/utils/convertDateToISO";
import {SharedModule} from "@app/shared/shared.module";
import {Role} from "@app/shared/types/role.enum";
import {IUserData} from "@app/shared/types/employee.type";

@Component({
  selector: "app-dashboard",
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.scss"],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgxSkeletonLoaderModule,
    TranslateModule,
    SelectComponent,
    SharedModule,
    SalesStatComponent,
    MatButtonModule,
    DatePickerComponent,
    DashboardSkeletonComponent,
  ],
  providers: [GeneralStatsService],
})
export class DashboardComponent implements OnInit {
  public EmergencyMode!: boolean;
  public RolesEnum = Role;
  public userRoles!: Role[];

  public sellersSelectOptions: SelectItemS = [];
  public form: FormGroup;
  public user: IUserData;
  private _qParams: Record<string, string>;

  // dependencies
  private _userService = inject(UserService);
  private _branchService = inject(BranchService);
  private _activatedRoute = inject(ActivatedRoute);
  private _generalStatsService = inject(GeneralStatsService);
  private _layoutService = inject(LayoutService);
  private _router = inject(Router);

  constructor() {
    // get user roles
    effect(() => {
      this.userRoles = this._userService.userSignal().roles || [];
    });

    // get user info
    effect(() => {
      this.user = this._userService.userSignal();
    });

    this._branchService.branchInfo$.subscribe((res) => {
      this.EmergencyMode = res?.is_emergency || false;
    });
    // set params
    this._activatedRoute.queryParams.subscribe((qParams) => {
      this._qParams = qParams;
    });
  }

  ngOnInit() {
    this._activatedRoute.data.subscribe((data) => {
      this._layoutService.pageTitle.set(data["pageTitle"]);
    });
    this._buildForm();
    this._getSellers();
    this._patchFormFieldsValues();
  }

  private _buildForm() {
    this.form = new FormGroup({
      seller: new FormControl(
        this._activatedRoute.snapshot.queryParams["seller_id"] || ""
      ),
      fromDate: new FormControl(
        this._activatedRoute.snapshot.queryParams["from"] || ""
      ),
      toDate: new FormControl(
        this._activatedRoute.snapshot.queryParams["to"] || ""
      ),
    });
  }

  private _getSellers() {
    this._generalStatsService.getSellers().subscribe((res) => {
      this.sellersSelectOptions = [{key: "همه", value: ""}].concat(
        res.map((item) => ({
          value: item.id,
          key: item?.profile?.first_name?.concat(
            ` ${item?.profile?.last_name}`
          ),
        }))
      );
    });
  }

  private _patchFormFieldsValues() {
    if (Object.keys(this._qParams).length) {
      setTimeout(() => {
        this.form.patchValue({
          ...this._qParams,
          fromDate: this._qParams["from"] ? this._qParams["from"] : null,
          toDate: this._qParams["to"] ? this._qParams["to"] : null,
        });
      }, 100);
    }
  }

  public submitForm() {
    this._router.navigate([], {
      queryParams: {
        seller_id: this.form.value.seller,
        from: this.form.value.fromDate
          ? convertDateToISO(this.form.value.fromDate)
          : null,
        to: this.form.value.toDate
          ? convertDateToISO(this.form.value.toDate)
          : null,
      },
    });
  }
}
